import type { SigninRedirectArgs, UserManager } from 'oidc-client-ts';
import { defineStore } from 'pinia';

function extractClaimsFromAccessToken(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return {};
  }
}

export const useOidcStore = defineStore('oidc', {
  state: () => ({
    loading: false,
    access_token: null as string | null,
    id_token: null,
    refresh_token: null,
    expires_at: null,
    profile: null as Record<string, unknown> | null,
    token_type: null,
    state: null,
    error: null as Error | null,
    userManager: {} as UserManager,
    url_state: null,
  }),
  actions: {
    async signinRedirectCallback() {
      try {
        return await this.userManager.signinRedirectCallback();
      } catch (error) {
        console.error(error);
        this.error = error as Error;
      }
    },
    async signinRedirect(args: SigninRedirectArgs | undefined = undefined) {
      try {
        await this.userManager.signinRedirect(args);
      } catch (error) {
        console.error(error);
        this.error = error as Error;
      }
    },
    async getUser() {
      this.error = null;

      try {
        const user = await this.userManager.getUser();

        Object.assign(this, user);

        return this.profile;
      } catch (error) {
        this.error = error as Error;
      }
    },
    async signinSilentCallback() {
      try {
        const user = await this.userManager.signinSilentCallback();
        Object.assign(this, user);
      } catch (error) {
        this.error = error as Error;
      }
    },
    async isAuthenticated() {
      const user = await this.userManager.getUser();

      return user !== null;
    },
    bindEvents() {
      this.userManager.events.addUserLoaded((user) => {
        Object.assign(this, user);
      });

      this.userManager.events.addUserSignedIn(() => {
        console.log('user signed in');
      });

      this.userManager.events.addUserSessionChanged(() => {
        console.log('user session changed');
      });

      this.userManager.events.addAccessTokenExpired(async () => {
        console.log('access token expired');
        try {
          if (!(await this.userManager.signinSilent())) {
            await this.userManager.signinRedirect();
          }
        } catch (e) {
          console.error(e);
          await this.userManager.signinRedirect();
        }
      });
    },
    async setUserManager(userManager: UserManager) {
      this.userManager = userManager;

      Object.assign(this, await userManager.getUser());

      this.bindEvents();
    },
  },
  getters: {
    /**
     * The employeeNumber claim from the access token, mapped from LDAP.
     * This is the user's database ID (User::getId()).
     */
    employeeNumber(): string | null {
      if (!this.access_token) return null;
      const claims = extractClaimsFromAccessToken(this.access_token);
      return (claims.employeeNumber as string) ?? null;
    },
  },
});
